import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { useRBAC } from '../../contexts/RBACContext';
import { fetchRoles, fetchPermissions, fetchAllRolePermissions, saveRolePermissions, createRole, deleteRole, fetchUsersWithRoles, assignUserRole, removeUserRole, linkUserToEmployee, unlinkUserFromEmployee, fetchEmployeePool } from '../../services/rbacService';
import { logAuditEvent, AUDIT_ACTIONS, AUDIT_ENTITIES } from '../../services/auditLogService';
import { supabase } from '../../lib/supabase';
import WorkflowNotificationPanel from './components/WorkflowNotificationPanel';

// ---- Module display config — covers ALL modules in the system ----
const MODULE_LABELS = {
  service_calculator:         { label: 'Service Calculator',          icon: 'Calculator',     color: 'text-blue-500',    group: 'Sales' },
  saved_calculations:         { label: 'Saved Calculations / Deals',  icon: 'BookOpen',       color: 'text-indigo-500',  group: 'Sales' },
  incentive_tickets:          { label: 'Project Incentive Tickets',   icon: 'Ticket',         color: 'text-violet-500',  group: 'Incentives' },
  team_assignment:            { label: 'Team Assignment',             icon: 'UserPlus',       color: 'text-purple-500',  group: 'Incentives' },
  pending_approval:           { label: 'Pending Approval',            icon: 'ClipboardCheck', color: 'text-amber-500',   group: 'Incentives' },
  finance_payments:           { label: 'Finance Payments',            icon: 'Banknote',       color: 'text-emerald-500', group: 'Finance' },
  finance_payment_report:     { label: 'Finance Payment Report',      icon: 'FileText',       color: 'text-teal-500',    group: 'Finance' },
  employee_incentive_reports: { label: 'Employee Incentive Reports',  icon: 'BarChart2',      color: 'text-cyan-500',    group: 'Reports' },
  analytics_dashboard:        { label: 'Analytics Dashboard',         icon: 'BarChart3',      color: 'text-sky-500',     group: 'Reports' },
  master_data:                { label: 'Master Data',                 icon: 'Database',       color: 'text-orange-500',  group: 'Administration' },
  tender_management:          { label: 'Tender Management',           icon: 'FileSearch',     color: 'text-lime-500',    group: 'Tender' },
  users:                      { label: 'Users',                       icon: 'Users',          color: 'text-rose-500',    group: 'Administration' },
  settings:                   { label: 'Settings',                    icon: 'Settings',       color: 'text-slate-500',   group: 'Administration' },
  roles_permissions:          { label: 'Roles & Permissions',         icon: 'ShieldCheck',    color: 'text-red-500',     group: 'Administration' },
};

// Canonical module order (known modules shown first, new ones appended automatically)
const MODULE_ORDER = [
  'service_calculator',
  'saved_calculations',
  'incentive_tickets',
  'team_assignment',
  'pending_approval',
  'finance_payments',
  'finance_payment_report',
  'employee_incentive_reports',
  'analytics_dashboard',
  'master_data',
  'tender_management',
  'users',
  'settings',
  'roles_permissions',
];

// Auto-generate a display config for any module not in MODULE_LABELS
const getModuleConfig = (moduleKey) => {
  if (MODULE_LABELS?.[moduleKey]) return MODULE_LABELS?.[moduleKey];
  // Auto-generate label and config for unknown/new modules
  const label = moduleKey
    ?.split('_')
    ?.map(w => w?.charAt(0)?.toUpperCase() + w?.slice(1))
    ?.join(' ');
  return { label, icon: 'Box', color: 'text-muted-foreground', group: 'Other' };
};

// ---- Permission display config ----
const PERM_LABELS = {
  view:                    { label: 'View',              icon: 'Eye',           category: 'core' },
  view_own:                { label: 'View Own',          icon: 'User',          category: 'core' },
  view_all:                { label: 'View All',          icon: 'Globe',         category: 'core' },
  view_team:               { label: 'View Team',         icon: 'Users',         category: 'core' },
  view_finance_only:       { label: 'Finance Only',      icon: 'Banknote',      category: 'core' },
  create:                  { label: 'Create',            icon: 'Plus',          category: 'core' },
  edit:                    { label: 'Edit',              icon: 'Pencil',        category: 'core' },
  delete:                  { label: 'Delete',            icon: 'Trash2',        category: 'core' },
  submit:                  { label: 'Submit',            icon: 'Send',          category: 'workflow' },
  approve:                 { label: 'Approve',           icon: 'CheckCircle2',  category: 'workflow' },
  reject:                  { label: 'Reject',            icon: 'XCircle',       category: 'workflow' },
  hold:                    { label: 'Hold',              icon: 'PauseCircle',   category: 'workflow' },
  return_for_edit:         { label: 'Return',            icon: 'RotateCcw',     category: 'workflow' },
  mark_close_won:          { label: 'Close Won',         icon: 'Trophy',        category: 'workflow' },
  create_incentive_ticket: { label: 'Create Ticket',     icon: 'TicketPlus',    category: 'workflow' },
  add_team_member:         { label: 'Add Member',        icon: 'UserPlus',      category: 'workflow' },
  remove_team_member:      { label: 'Remove Member',     icon: 'UserMinus',     category: 'workflow' },
  validate_calculate:      { label: 'Calculate',         icon: 'Calculator',    category: 'workflow' },
  mark_ready_for_payment:  { label: 'Ready for Pay',     icon: 'Clock',         category: 'workflow' },
  mark_paid:               { label: 'Mark Paid',         icon: 'BadgeCheck',    category: 'workflow' },
  export:                  { label: 'Export',            icon: 'Download',      category: 'export' },
  export_pdf:              { label: 'Export PDF',        icon: 'FileDown',      category: 'export' },
  export_excel:            { label: 'Export Excel',      icon: 'Sheet',         category: 'export' },
  print:                   { label: 'Print',             icon: 'Printer',       category: 'export' },
  manage_users:            { label: 'Manage Users',      icon: 'UserCog',       category: 'admin' },
  manage_roles:            { label: 'Manage Roles',      icon: 'ShieldCheck',   category: 'admin' },
  manage_settings:         { label: 'Manage Settings',   icon: 'Settings2',     category: 'admin' },
  reset_password:          { label: 'Reset Password',    icon: 'KeyRound',      category: 'admin' },
};

const PERM_CATEGORY_ORDER = ['core', 'workflow', 'export', 'admin'];
const PERM_CATEGORY_LABELS = {
  core:     { label: 'Access',    color: 'text-blue-500',    bg: 'bg-blue-500/10' },
  workflow: { label: 'Workflow',  color: 'text-violet-500',  bg: 'bg-violet-500/10' },
  export:   { label: 'Export',    color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
  admin:    { label: 'Admin',     color: 'text-rose-500',    bg: 'bg-rose-500/10' },
};

const ROLE_COLORS = {
  SUPER_ADMIN:    { bg: 'bg-red-500/10',     text: 'text-red-500',     border: 'border-red-500/30',     icon: 'Crown' },
  ADMIN:          { bg: 'bg-violet-500/10',  text: 'text-violet-500',  border: 'border-violet-500/30',  icon: 'ShieldCheck' },
  SALES_USER:     { bg: 'bg-blue-500/10',    text: 'text-blue-500',    border: 'border-blue-500/30',    icon: 'TrendingUp' },
  SALES_MANAGER:  { bg: 'bg-indigo-500/10',  text: 'text-indigo-500',  border: 'border-indigo-500/30',  icon: 'BarChart2' },
  PROJECT_MANAGER:{ bg: 'bg-purple-500/10',  text: 'text-purple-500',  border: 'border-purple-500/30',  icon: 'FolderKanban' },
  APPROVER:       { bg: 'bg-amber-500/10',   text: 'text-amber-500',   border: 'border-amber-500/30',   icon: 'ClipboardCheck' },
  FINANCE:        { bg: 'bg-emerald-500/10', text: 'text-emerald-500', border: 'border-emerald-500/30', icon: 'Banknote' },
  HR_MASTER_DATA: { bg: 'bg-teal-500/10',    text: 'text-teal-500',    border: 'border-teal-500/30',    icon: 'Database' },
  EMPLOYEE:       { bg: 'bg-sky-500/10',     text: 'text-sky-500',     border: 'border-sky-500/30',     icon: 'User' },
  AUDITOR:        { bg: 'bg-slate-500/10',   text: 'text-slate-500',   border: 'border-slate-500/30',   icon: 'Search' },
};

const getRoleColor = (roleKey) =>
  ROLE_COLORS?.[roleKey] || { bg: 'bg-muted', text: 'text-muted-foreground', border: 'border-border', icon: 'Tag' };

const DEFAULT_ADD_USER = { email: '', password: '', full_name: '', phone: '', roleId: '' };

// ============================================================
// MAIN COMPONENT
// ============================================================
const RolePermissionManagement = () => {
  const { user, userProfile } = useAuth();
  const { isAdmin, hasPermission, reloadRbac } = useRBAC();

  const [activeTab, setActiveTab] = useState('matrix');
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [allRolePerms, setAllRolePerms] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [pendingChanges, setPendingChanges] = useState({});

  // New role form
  const [showNewRole, setShowNewRole] = useState(false);
  const [newRoleKey, setNewRoleKey] = useState('');
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDesc, setNewRoleDesc] = useState('');
  const [newRoleErr, setNewRoleErr] = useState('');
  const [creatingRole, setCreatingRole] = useState(false);

  // Users tab
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [employeePool, setEmployeePool] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [expandedUser, setExpandedUser] = useState(null);
  const [userActionMsg, setUserActionMsg] = useState({ type: '', text: '' });

  // Add New User modal
  const [showAddUser, setShowAddUser] = useState(false);
  const [addUserData, setAddUserData] = useState(DEFAULT_ADD_USER);
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [addUserErr, setAddUserErr] = useState('');
  const [showAddUserPassword, setShowAddUserPassword] = useState(false);

  // Reset Password modal
  const [resetPasswordTarget, setResetPasswordTarget] = useState(null);
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [resetPasswordErr, setResetPasswordErr] = useState('');
  const [showResetPasswordInput, setShowResetPasswordInput] = useState(false);

  // ---- Load data ----
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesData, permsData, rolePermsData] = await Promise.all([
        fetchRoles(),
        fetchPermissions(),
        fetchAllRolePermissions(),
      ]);
      setRoles(rolesData);
      setPermissions(permsData);
      setAllRolePerms(rolePermsData);
      if (!selectedRoleId && rolesData?.length > 0) {
        setSelectedRoleId(rolesData?.[0]?.id);
      }
    } catch (err) {
      setMsg({ type: 'error', text: err?.message || 'Failed to load data' });
    } finally {
      setLoading(false);
    }
  }, [selectedRoleId]);

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    if (activeTab === 'users' || activeTab === 'employees') loadUsersTab();
  }, [activeTab]);

  const loadUsersTab = async () => {
    setUsersLoading(true);
    try {
      const [usersData, empData] = await Promise.all([fetchUsersWithRoles(), fetchEmployeePool()]);
      setUsers(usersData);
      setEmployeePool(empData);
    } catch (err) {
      setUserActionMsg({ type: 'error', text: 'Failed to load users: ' + err?.message });
    } finally {
      setUsersLoading(false);
    }
  };

  // ---- Derived: group permissions by module (sorted by MODULE_ORDER) ----
  const permsByModule = permissions?.reduce((acc, p) => {
    if (!acc?.[p?.module_key]) acc[p?.module_key] = [];
    acc?.[p?.module_key]?.push(p);
    return acc;
  }, {});

  // Sort modules by canonical order, then alphabetically for any extras
  const sortedModuleKeys = [
    ...MODULE_ORDER?.filter(k => permsByModule?.[k]),
    ...Object.keys(permsByModule || {})?.filter(k => !MODULE_ORDER?.includes(k))?.sort(),
  ];

  // ---- Check if role has permission (with pending changes) ----
  const isPermAllowed = (permId) => {
    if (pendingChanges?.hasOwnProperty(permId)) return pendingChanges?.[permId];
    return allRolePerms?.some(rp => rp?.role_id === selectedRoleId && rp?.permission_id === permId && rp?.is_allowed);
  };

  const togglePerm = (permId) => {
    const current = isPermAllowed(permId);
    setPendingChanges(prev => ({ ...prev, [permId]: !current }));
    setMsg({ type: '', text: '' });
  };

  const setAllModulePerms = (moduleKey, value) => {
    const modulePerms = permsByModule?.[moduleKey] || [];
    const updates = {};
    modulePerms?.forEach(p => { updates[p?.id] = value; });
    setPendingChanges(prev => ({ ...prev, ...updates }));
    setMsg({ type: '', text: '' });
  };

  // ---- Save permissions ----
  const handleSave = async () => {
    if (!selectedRoleId) return;
    setSaving(true);
    setMsg({ type: '', text: '' });
    try {
      const allowedIds = permissions?.filter(p => isPermAllowed(p?.id))?.map(p => p?.id);
      const allIds = permissions?.map(p => p?.id);

      // Save to database
      await saveRolePermissions(selectedRoleId, allowedIds, allIds);

      // Re-fetch the latest state from DB to confirm save was committed
      const updated = await fetchAllRolePermissions();
      setAllRolePerms(updated);
      setPendingChanges({});

      // Verify the save actually persisted (spot-check one allowed permission)
      const savedAllowedCount = updated?.filter(
        rp => rp?.role_id === selectedRoleId && rp?.is_allowed
      )?.length;

      setMsg({
        type: 'success',
        text: `Permissions saved successfully (${savedAllowedCount} active permissions). Changes are now live for all users with this role.`,
      });
      setTimeout(() => setMsg({ type: '', text: '' }), 5000);

      // Reload the current user's RBAC context so sidebar and profile sync instantly
      await reloadRbac();

      logAuditEvent({
        user, userProfile,
        action: AUDIT_ACTIONS?.PERMISSION_SAVED,
        entityType: AUDIT_ENTITIES?.PERMISSION,
        entityName: roles?.find(r => r?.id === selectedRoleId)?.role_name,
        newValue: { allowedCount: allowedIds?.length, savedCount: savedAllowedCount },
      });
    } catch (err) {
      console.error('[Permission Matrix] Save failed:', err);
      setMsg({ type: 'error', text: `Save failed: ${err?.message || 'Unknown error. Please try again.'}` });
    } finally {
      setSaving(false);
    }
  };

  // ---- Create role ----
  const handleCreateRole = async () => {
    setNewRoleErr('');
    if (!newRoleName?.trim()) { setNewRoleErr('Role name is required.'); return; }
    const key = newRoleKey?.trim() || newRoleName?.trim()?.toUpperCase()?.replace(/\s+/g, '_');
    if (roles?.some(r => r?.role_key === key)) { setNewRoleErr('A role with this key already exists.'); return; }
    setCreatingRole(true);
    try {
      const created = await createRole({ roleKey: key, roleName: newRoleName?.trim(), description: newRoleDesc?.trim() });
      setRoles(prev => [...prev, created]);
      setSelectedRoleId(created?.id);
      setShowNewRole(false);
      setNewRoleKey(''); setNewRoleName(''); setNewRoleDesc('');
      setMsg({ type: 'success', text: `Role "${created?.role_name}" created.` });
      setTimeout(() => setMsg({ type: '', text: '' }), 3000);
    } catch (err) {
      setNewRoleErr(err?.message || 'Failed to create role.');
    } finally {
      setCreatingRole(false);
    }
  };

  // ---- Delete role ----
  const handleDeleteRole = async (role) => {
    if (role?.is_system_role) return;
    if (!window.confirm(`Delete role "${role?.role_name}"? This cannot be undone.`)) return;
    try {
      await deleteRole(role?.id);
      setRoles(prev => prev?.filter(r => r?.id !== role?.id));
      if (selectedRoleId === role?.id) setSelectedRoleId(roles?.[0]?.id || null);
      setMsg({ type: 'success', text: `Role "${role?.role_name}" deleted.` });
      setTimeout(() => setMsg({ type: '', text: '' }), 3000);
    } catch (err) {
      setMsg({ type: 'error', text: err?.message || 'Failed to delete role.' });
    }
  };

  // ---- Assign/remove user role ----
  const handleAssignRole = async (userId, roleId, action) => {
    try {
      if (action === 'add') {
        await assignUserRole(userId, roleId);
      } else {
        await removeUserRole(userId, roleId);
      }
      await loadUsersTab();
      setUserActionMsg({ type: 'success', text: action === 'add' ? 'Role assigned successfully.' : 'Role removed successfully.' });
      setTimeout(() => setUserActionMsg({ type: '', text: '' }), 3000);
    } catch (err) {
      setUserActionMsg({ type: 'error', text: 'Error: ' + err?.message });
    }
  };

  // ---- Link user to employee ----
  const handleLinkEmployee = async (userId, employeeId) => {
    try {
      if (employeeId) {
        await linkUserToEmployee(userId, employeeId);
      } else {
        await unlinkUserFromEmployee(userId);
      }
      await loadUsersTab();
      setUserActionMsg({ type: 'success', text: employeeId ? 'Employee linked successfully.' : 'Employee unlinked.' });
      setTimeout(() => setUserActionMsg({ type: '', text: '' }), 3000);
    } catch (err) {
      setUserActionMsg({ type: 'error', text: 'Error: ' + err?.message });
    }
  };

  // ---- Add New User ----
  const handleOpenAddUser = () => {
    setAddUserData(DEFAULT_ADD_USER);
    setAddUserErr('');
    setShowAddUserPassword(false);
    setShowAddUser(true);
  };

  const handleAddUser = async (e) => {
    e?.preventDefault();
    setAddUserErr('');
    if (!addUserData?.email?.trim()) { setAddUserErr('Email is required.'); return; }
    if (!addUserData?.password || addUserData?.password?.length < 8) { setAddUserErr('Password must be at least 8 characters.'); return; }
    if (!addUserData?.full_name?.trim()) { setAddUserErr('Full name is required.'); return; }
    setAddUserLoading(true);
    try {
      const { data: rpcResult, error: rpcError } = await supabase?.rpc('admin_create_user', {
        p_email: addUserData?.email?.trim()?.toLowerCase(),
        p_password: addUserData?.password,
        p_full_name: addUserData?.full_name?.trim(),
        p_phone: addUserData?.phone?.trim() || null,
        p_role_id: addUserData?.roleId || null,
      });
      if (rpcError) throw rpcError;
      if (rpcResult?.success === false) throw new Error(rpcResult?.error || 'Failed to create user.');
      setUserActionMsg({ type: 'success', text: `User "${addUserData?.email}" created successfully.` });
      setTimeout(() => setUserActionMsg({ type: '', text: '' }), 4000);
      setShowAddUser(false);
      await loadUsersTab();
      logAuditEvent({
        user, userProfile,
        action: AUDIT_ACTIONS?.USER_ADDED_AS_ADMIN || 'user_created',
        entityType: AUDIT_ENTITIES?.USER,
        entityName: addUserData?.email?.trim()?.toLowerCase(),
        newValue: { roleId: addUserData?.roleId, createdUserId: rpcResult?.user_id },
      });
    } catch (err) {
      setAddUserErr(err?.message || 'Failed to create user. Please try again.');
    } finally {
      setAddUserLoading(false);
    }
  };

  // ---- Reset Password ----
  const handleOpenResetPassword = (targetUser) => {
    setResetPasswordTarget(targetUser);
    setResetPasswordValue('');
    setResetPasswordErr('');
    setShowResetPasswordInput(false);
    setShowResetPassword(true);
  };

  const handleResetPassword = async (e) => {
    e?.preventDefault();
    setResetPasswordErr('');
    if (!resetPasswordValue || resetPasswordValue?.length < 8) {
      setResetPasswordErr('Password must be at least 8 characters.');
      return;
    }
    setResetPasswordLoading(true);
    try {
      const { data, error } = await supabase?.rpc('admin_reset_user_password', {
        target_user_id: resetPasswordTarget?.id,
        new_password: resetPasswordValue,
      });
      if (error) throw error;
      if (data?.success === false) throw new Error(data?.error || 'Failed to reset password.');
      setShowResetPassword(false);
      setUserActionMsg({ type: 'success', text: `Password for "${resetPasswordTarget?.full_name || resetPasswordTarget?.email}" has been reset successfully.` });
      setTimeout(() => setUserActionMsg({ type: '', text: '' }), 4000);
      logAuditEvent({
        user, userProfile,
        action: 'password_reset',
        entityType: AUDIT_ENTITIES?.USER,
        entityName: resetPasswordTarget?.email,
        newValue: { resetBy: user?.id },
      });
    } catch (err) {
      setResetPasswordErr(err?.message || 'Failed to reset password. Please try again.');
    } finally {
      setResetPasswordLoading(false);
    }
  };

  // ---- Toggle user active status ----
  const handleToggleUserActive = async (targetUser) => {
    try {
      const newActive = !targetUser?.is_active;
      const { error } = await supabase?.from('user_profiles')?.update({
        is_active: newActive,
        updated_at: new Date()?.toISOString(),
      })?.eq('id', targetUser?.id);
      if (error) throw error;
      await loadUsersTab();
      setUserActionMsg({ type: 'success', text: `${targetUser?.email} has been ${newActive ? 'activated' : 'deactivated'}.` });
      setTimeout(() => setUserActionMsg({ type: '', text: '' }), 3000);
    } catch (err) {
      setUserActionMsg({ type: 'error', text: 'Error: ' + err?.message });
    }
  };

  const countRolePerms = (roleId) => allRolePerms?.filter(rp => rp?.role_id === roleId && rp?.is_allowed)?.length;

  const selectedRole = roles?.find(r => r?.id === selectedRoleId);
  const isSystemRole = selectedRole?.is_system_role;
  const hasPendingChanges = Object.keys(pendingChanges)?.length > 0;
  const canResetPassword = isAdmin || hasPermission('users', 'reset_password');

  const filteredUsers = users?.filter(u =>
    !userSearch ||
    u?.email?.toLowerCase()?.includes(userSearch?.toLowerCase()) ||
    u?.full_name?.toLowerCase()?.includes(userSearch?.toLowerCase())
  );

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* ---- Header ---- */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="ShieldCheck" size={16} className="text-primary" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">Roles & Permissions</h1>
            </div>
            <p className="text-sm text-muted-foreground">
              Single source of truth for all user roles, module access, and permission control
            </p>
          </div>
          <div className="flex items-center gap-2">
            {activeTab === 'users' && (
              <Button onClick={handleOpenAddUser} iconName="UserPlus" iconPosition="left">
                Add New User
              </Button>
            )}
            {activeTab === 'matrix' && (
              <Button variant="outline" onClick={() => setShowNewRole(true)} iconName="Plus" iconPosition="left">
                New Role
              </Button>
            )}
          </div>
        </div>

        {/* ---- Tabs ---- */}
        <div className="flex gap-1 border-b border-border">
          {[
            { id: 'matrix',    label: 'Permission Matrix', icon: 'Grid3X3' },
            { id: 'users',     label: 'Users & Roles',     icon: 'Users' },
            { id: 'employees', label: 'Employee Links',    icon: 'Link' },
            { id: 'workflow',  label: 'Workflow Notifications', icon: 'GitBranch' },
          ]?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-all ${
                activeTab === tab?.id
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={15} />
              {tab?.label}
            </button>
          ))}
        </div>

        {/* ---- Global message ---- */}
        {msg?.text && (
          <div className={`flex items-center gap-2 rounded-xl px-4 py-3 text-sm border ${
            msg?.type === 'success' ?'bg-success/10 border-success/30 text-success' :'bg-error/10 border-error/30 text-error'
          }`}>
            <Icon name={msg?.type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={15} />
            {msg?.text}
          </div>
        )}

        {/* ============================================================ */}
        {/* TAB: WORKFLOW NOTIFICATIONS */}
        {/* ============================================================ */}
        {activeTab === 'workflow' && (
          <WorkflowNotificationPanel />
        )}

        {/* ============================================================ */}
        {/* TAB: PERMISSION MATRIX */}
        {/* ============================================================ */}
        {activeTab === 'matrix' && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">

            {/* ---- Role list (left panel) ---- */}
            <div className="lg:col-span-1 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-1 mb-3">Select Role</p>
              {roles?.map(role => {
                const rc = getRoleColor(role?.role_key);
                const count = countRolePerms(role?.id);
                const isSelected = selectedRoleId === role?.id;
                return (
                  <div
                    key={role?.id}
                    onClick={() => { setSelectedRoleId(role?.id); setPendingChanges({}); }}
                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                      isSelected
                        ? `${rc?.bg} ${rc?.border} shadow-sm`
                        : 'bg-card border-border hover:border-primary/30 hover:bg-muted/30'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${rc?.bg}`}>
                      <Icon name={rc?.icon} size={15} className={rc?.text} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-semibold truncate ${isSelected ? rc?.text : 'text-foreground'}`}>
                        {role?.role_name}
                      </p>
                      <p className="text-xs text-muted-foreground">{count} permissions</p>
                    </div>
                    {!role?.is_system_role && (
                      <button
                        onClick={e => { e?.stopPropagation(); handleDeleteRole(role); }}
                        className="w-6 h-6 flex items-center justify-center rounded hover:bg-error/10 text-muted-foreground hover:text-error transition-colors flex-shrink-0"
                        title="Delete role"
                      >
                        <Icon name="Trash2" size={12} />
                      </button>
                    )}
                  </div>
                );
              })}

              {/* New role form */}
              {showNewRole && (
                <div className="p-4 bg-card border border-border rounded-xl space-y-3 mt-2">
                  <p className="text-sm font-semibold text-foreground">Create New Role</p>
                  <input
                    type="text"
                    value={newRoleName}
                    onChange={e => { setNewRoleName(e?.target?.value); setNewRoleErr(''); }}
                    placeholder="Role name (e.g. Reviewer)"
                    className="w-full h-9 px-3 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                  />
                  <input
                    type="text"
                    value={newRoleKey}
                    onChange={e => setNewRoleKey(e?.target?.value?.toUpperCase()?.replace(/\s+/g, '_'))}
                    placeholder="Role key (auto-generated if empty)"
                    className="w-full h-9 px-3 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                  />
                  <input
                    type="text"
                    value={newRoleDesc}
                    onChange={e => setNewRoleDesc(e?.target?.value)}
                    placeholder="Description (optional)"
                    className="w-full h-9 px-3 rounded-lg border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                  />
                  {newRoleErr && <p className="text-xs text-error">{newRoleErr}</p>}
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleCreateRole} loading={creatingRole} className="flex-1">Create</Button>
                    <Button size="sm" variant="outline" onClick={() => { setShowNewRole(false); setNewRoleKey(''); setNewRoleName(''); setNewRoleDesc(''); setNewRoleErr(''); }}>Cancel</Button>
                  </div>
                </div>
              )}
            </div>

            {/* ---- Permission editor (right panel) ---- */}
            <div className="lg:col-span-3 space-y-4">
              {selectedRole ? (
                <>
                  {/* Role header bar */}
                  <div className={`flex items-center justify-between p-4 rounded-xl border ${getRoleColor(selectedRole?.role_key)?.bg} ${getRoleColor(selectedRole?.role_key)?.border}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${getRoleColor(selectedRole?.role_key)?.bg}`}>
                        <Icon name={getRoleColor(selectedRole?.role_key)?.icon} size={20} className={getRoleColor(selectedRole?.role_key)?.text} />
                      </div>
                      <div>
                        <h3 className={`text-base font-bold ${getRoleColor(selectedRole?.role_key)?.text}`}>{selectedRole?.role_name}</h3>
                        <p className="text-xs text-muted-foreground">
                          {selectedRole?.description || selectedRole?.role_key}
                          {isSystemRole && (
                            <span className="ml-2 px-1.5 py-0.5 bg-muted rounded text-xs">System Role</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {hasPendingChanges && (
                        <span className="text-xs text-warning bg-warning/10 px-2 py-1 rounded-lg border border-warning/20">
                          Unsaved changes
                        </span>
                      )}
                      <Button
                        size="sm"
                        onClick={handleSave}
                        loading={saving}
                        disabled={saving || !hasPendingChanges}
                        iconName="Save"
                        iconPosition="left"
                      >
                        Save
                      </Button>
                    </div>
                  </div>

                  {/* Legend */}
                  <div className="flex flex-wrap items-center gap-3 px-1">
                    <span className="text-xs text-muted-foreground font-medium">Permission types:</span>
                    {PERM_CATEGORY_ORDER?.map(cat => {
                      const catConfig = PERM_CATEGORY_LABELS?.[cat];
                      return (
                        <span key={cat} className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${catConfig?.bg} ${catConfig?.color}`}>
                          {catConfig?.label}
                        </span>
                      );
                    })}
                  </div>

                  {/* Module permission blocks */}
                  <div className="space-y-3">
                    {sortedModuleKeys?.map(moduleKey => {
                      const modulePerms = permsByModule?.[moduleKey] || [];
                      const modConfig = getModuleConfig(moduleKey);
                      const allowedCount = modulePerms?.filter(p => isPermAllowed(p?.id))?.length;
                      const allOn = modulePerms?.every(p => isPermAllowed(p?.id));
                      const allOff = modulePerms?.every(p => !isPermAllowed(p?.id));

                      // Group permissions by category
                      const permsByCategory = modulePerms?.reduce((acc, p) => {
                        const cat = PERM_LABELS?.[p?.permission_key]?.category || 'core';
                        if (!acc?.[cat]) acc[cat] = [];
                        acc?.[cat]?.push(p);
                        return acc;
                      }, {});

                      return (
                        <div key={moduleKey} className="bg-card border border-border rounded-xl overflow-hidden">
                          {/* Module header */}
                          <div className="flex items-center justify-between px-4 py-3 bg-muted/20 border-b border-border">
                            <div className="flex items-center gap-2.5">
                              <div className={`w-7 h-7 rounded-lg flex items-center justify-center bg-background border border-border`}>
                                <Icon name={modConfig?.icon} size={14} className={modConfig?.color} />
                              </div>
                              <div>
                                <span className="text-sm font-semibold text-foreground">{modConfig?.label}</span>
                                <span className="ml-2 text-xs text-muted-foreground">
                                  {allowedCount}/{modulePerms?.length} granted
                                </span>
                              </div>
                              {/* Progress bar */}
                              <div className="hidden sm:flex items-center gap-1.5 ml-2">
                                <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-primary rounded-full transition-all"
                                    style={{ width: modulePerms?.length > 0 ? `${(allowedCount / modulePerms?.length) * 100}%` : '0%' }}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={() => setAllModulePerms(moduleKey, true)}
                                disabled={allOn}
                                className="text-xs px-2 py-1 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                              >All</button>
                              <button
                                onClick={() => setAllModulePerms(moduleKey, false)}
                                disabled={allOff}
                                className="text-xs px-2 py-1 rounded-lg bg-muted text-muted-foreground hover:bg-muted/80 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                              >None</button>
                            </div>
                          </div>

                          {/* Permissions grouped by category */}
                          <div className="px-4 py-3 space-y-3">
                            {PERM_CATEGORY_ORDER?.map(cat => {
                              const catPerms = permsByCategory?.[cat];
                              if (!catPerms?.length) return null;
                              const catConfig = PERM_CATEGORY_LABELS?.[cat];
                              return (
                                <div key={cat}>
                                  <p className={`text-[10px] font-semibold uppercase tracking-wider mb-1.5 ${catConfig?.color}`}>
                                    {catConfig?.label}
                                  </p>
                                  <div className="flex flex-wrap gap-1.5">
                                    {catPerms?.map(perm => {
                                      const allowed = isPermAllowed(perm?.id);
                                      const permConfig = PERM_LABELS?.[perm?.permission_key] || { label: perm?.permission_name, icon: 'Check' };
                                      return (
                                        <button
                                          key={perm?.id}
                                          onClick={() => togglePerm(perm?.id)}
                                          title={perm?.description || perm?.permission_name}
                                          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                                            allowed
                                              ? 'bg-primary/10 border-primary/30 text-primary shadow-sm'
                                              : 'bg-background border-border text-muted-foreground hover:border-primary/30 hover:text-foreground'
                                          }`}
                                        >
                                          <Icon name={allowed ? 'CheckSquare' : 'Square'} size={12} />
                                          <Icon name={permConfig?.icon} size={11} />
                                          {permConfig?.label}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Save footer */}
                  {hasPendingChanges && (
                    <div className="sticky bottom-4 flex items-center justify-between bg-card border border-border rounded-xl px-4 py-3 shadow-lg">
                      <p className="text-sm text-muted-foreground">You have unsaved permission changes.</p>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => setPendingChanges({})}>Discard</Button>
                        <Button size="sm" onClick={handleSave} loading={saving} disabled={saving} iconName="Save" iconPosition="left">
                          Save Changes
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <Icon name="ShieldOff" size={40} className="mb-3 opacity-30" />
                  <p className="text-sm">Select a role to manage its permissions</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* TAB: USERS & ROLES */}
        {/* ============================================================ */}
        {activeTab === 'users' && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="relative flex-1 max-w-sm">
                <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  value={userSearch}
                  onChange={e => setUserSearch(e?.target?.value)}
                  placeholder="Search users..."
                  className="w-full h-9 pl-9 pr-3 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                />
              </div>
              <button onClick={loadUsersTab} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
                <Icon name="RefreshCw" size={13} />
                Refresh
              </button>
            </div>

            {userActionMsg?.text && (
              <div className={`flex items-center gap-2 rounded-xl px-4 py-3 text-sm border ${
                userActionMsg?.type === 'error' ?'bg-error/10 border-error/30 text-error' :'bg-success/10 border-success/30 text-success'
              }`}>
                <Icon name={userActionMsg?.type === 'error' ? 'AlertCircle' : 'CheckCircle'} size={15} />
                {userActionMsg?.text}
              </div>
            )}

            {usersLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                <div className="px-5 py-3 border-b border-border bg-muted/20 flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">{filteredUsers?.length} users</span>
                  <span className="text-xs text-muted-foreground">Click a user to manage their roles</span>
                </div>
                <div className="divide-y divide-border/50">
                  {filteredUsers?.map(u => {
                    const isExpanded = expandedUser === u?.id;
                    return (
                      <div key={u?.id} className="hover:bg-muted/10 transition-colors">
                        <div
                          className="flex items-center gap-4 px-5 py-3.5 cursor-pointer"
                          onClick={() => setExpandedUser(isExpanded ? null : u?.id)}
                        >
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                            {(u?.full_name || u?.email || '?')?.charAt(0)?.toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{u?.full_name || '—'}</p>
                            <p className="text-xs text-muted-foreground truncate">{u?.email}</p>
                          </div>
                          <div className="flex flex-wrap gap-1 max-w-xs">
                            {u?.rbacRoles?.length > 0 ? u?.rbacRoles?.map(r => {
                              const rc = getRoleColor(r?.role_key);
                              return (
                                <span key={r?.role_key} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${rc?.bg} ${rc?.text}`}>
                                  <Icon name={rc?.icon} size={10} />
                                  {r?.role_name}
                                </span>
                              );
                            }) : (
                              <span className="text-xs text-muted-foreground italic">No role assigned</span>
                            )}
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full border flex-shrink-0 ${
                            u?.is_active !== false
                              ? 'bg-success/10 text-success border-success/20' :'bg-error/10 text-error border-error/20'
                          }`}>
                            {u?.is_active !== false ? 'Active' : 'Inactive'}
                          </span>
                          {u?.linkedEmployee && (
                            <span className="text-xs text-primary bg-primary/10 px-2 py-0.5 rounded-full border border-primary/20 flex-shrink-0 hidden sm:flex items-center gap-1">
                              <Icon name="Link" size={10} />
                              {u?.linkedEmployee?.full_name}
                            </span>
                          )}
                          <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={15} className="text-muted-foreground flex-shrink-0" />
                        </div>

                        {isExpanded && (
                          <div className="px-5 pb-4 space-y-4 bg-muted/5 border-t border-border/50">
                            <div>
                              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 mt-3">Assign Roles</p>
                              <div className="flex flex-wrap gap-2">
                                {roles?.map(role => {
                                  const rc = getRoleColor(role?.role_key);
                                  const hasRole = u?.rbacRoles?.some(r => r?.role_key === role?.role_key);
                                  return (
                                    <button
                                      key={role?.id}
                                      onClick={() => handleAssignRole(u?.id, role?.id, hasRole ? 'remove' : 'add')}
                                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                                        hasRole
                                          ? `${rc?.bg} ${rc?.border} ${rc?.text}`
                                          : 'bg-background border-border text-muted-foreground hover:border-primary/30'
                                      }`}
                                    >
                                      <Icon name={hasRole ? 'CheckSquare' : 'Square'} size={12} />
                                      {role?.role_name}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                            <div className="flex items-center gap-3 pt-1 flex-wrap">
                              <button
                                onClick={() => handleToggleUserActive(u)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${
                                  u?.is_active !== false
                                    ? 'bg-error/10 border-error/30 text-error hover:bg-error/20' :'bg-success/10 border-success/30 text-success hover:bg-success/20'
                                }`}
                              >
                                <Icon name={u?.is_active !== false ? 'UserX' : 'UserCheck'} size={12} />
                                {u?.is_active !== false ? 'Deactivate User' : 'Activate User'}
                              </button>
                              {canResetPassword && (
                                <button
                                  onClick={() => handleOpenResetPassword(u)}
                                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all bg-warning/10 border-warning/30 text-warning hover:bg-warning/20"
                                >
                                  <Icon name="KeyRound" size={12} />
                                  Reset Password
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {filteredUsers?.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                      <Icon name="Users" size={32} className="mb-3 opacity-30" />
                      <p className="text-sm">No users found</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ============================================================ */}
        {/* TAB: EMPLOYEE LINKS */}
        {/* ============================================================ */}
        {activeTab === 'employees' && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Link user accounts to employee records so the system can identify which deals, tickets, and reports belong to each user.
            </p>

            {userActionMsg?.text && (
              <div className={`flex items-center gap-2 rounded-xl px-4 py-3 text-sm border ${
                userActionMsg?.type === 'error' ?'bg-error/10 border-error/30 text-error' :'bg-success/10 border-success/30 text-success'
              }`}>
                <Icon name={userActionMsg?.type === 'error' ? 'AlertCircle' : 'CheckCircle'} size={15} />
                {userActionMsg?.text}
              </div>
            )}

            {usersLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                <div className="px-5 py-3 border-b border-border bg-muted/20">
                  <span className="text-sm font-medium text-foreground">User → Employee Links</span>
                </div>
                <div className="divide-y divide-border/50">
                  {users?.map(u => (
                    <div key={u?.id} className="flex items-center gap-4 px-5 py-3.5">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                        {(u?.full_name || u?.email || '?')?.charAt(0)?.toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{u?.full_name || u?.email}</p>
                        <p className="text-xs text-muted-foreground truncate">{u?.email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Icon name="Link" size={14} className="text-muted-foreground flex-shrink-0" />
                        <select
                          value={u?.linkedEmployee?.id || ''}
                          onChange={e => handleLinkEmployee(u?.id, e?.target?.value || null)}
                          className="h-8 px-2 rounded-lg border border-border bg-background text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors min-w-[180px]"
                        >
                          <option value="">— No employee linked —</option>
                          {employeePool?.map(emp => (
                            <option key={emp?.id} value={emp?.id}>
                              {emp?.full_name} {emp?.employee_code ? `(${emp?.employee_code})` : ''} — {emp?.department || 'General'}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ))}
                  {users?.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                      <Icon name="Link" size={32} className="mb-3 opacity-30" />
                      <p className="text-sm">No users to link</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {/* ============================================================ */}
      {/* ADD NEW USER MODAL */}
      {/* ============================================================ */}
      {showAddUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Icon name="UserPlus" size={18} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-foreground">Add New User</h2>
                  <p className="text-xs text-muted-foreground">Create account with role assignment</p>
                </div>
              </div>
              <button
                onClick={() => setShowAddUser(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
            <form onSubmit={handleAddUser} className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">Full Name <span className="text-error">*</span></label>
                <input
                  type="text"
                  value={addUserData?.full_name}
                  onChange={e => setAddUserData(prev => ({ ...prev, full_name: e?.target?.value }))}
                  placeholder="e.g. Mohammed Al-Rashidi"
                  className="w-full h-10 px-3 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">Email Address <span className="text-error">*</span></label>
                <input
                  type="email"
                  value={addUserData?.email}
                  onChange={e => setAddUserData(prev => ({ ...prev, email: e?.target?.value }))}
                  placeholder="user@ecity.com"
                  className="w-full h-10 px-3 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">Password <span className="text-error">*</span></label>
                <div className="relative">
                  <input
                    type={showAddUserPassword ? 'text' : 'password'}
                    value={addUserData?.password}
                    onChange={e => setAddUserData(prev => ({ ...prev, password: e?.target?.value }))}
                    placeholder="Minimum 8 characters"
                    className="w-full h-10 px-3 pr-10 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowAddUserPassword(p => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Icon name={showAddUserPassword ? 'EyeOff' : 'Eye'} size={15} />
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">Phone <span className="text-muted-foreground font-normal">(optional)</span></label>
                <input
                  type="tel"
                  value={addUserData?.phone}
                  onChange={e => setAddUserData(prev => ({ ...prev, phone: e?.target?.value }))}
                  placeholder="+968 XXXX XXXX"
                  className="w-full h-10 px-3 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">Assign Role <span className="text-muted-foreground font-normal">(optional)</span></label>
                <select
                  value={addUserData?.roleId}
                  onChange={e => setAddUserData(prev => ({ ...prev, roleId: e?.target?.value }))}
                  className="w-full h-10 px-3 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                >
                  <option value="">— No role (assign later) —</option>
                  {roles?.map(role => (
                    <option key={role?.id} value={role?.id}>{role?.role_name}</option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">The user's menu access will be determined by their assigned role.</p>
              </div>
              {addUserErr && (
                <div className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-xs border bg-error/10 border-error/30 text-error">
                  <Icon name="AlertCircle" size={13} />
                  {addUserErr}
                </div>
              )}
              <div className="flex gap-3 pt-1">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setShowAddUser(false)} disabled={addUserLoading}>
                  Cancel
                </Button>
                <Button type="submit" className="flex-1" loading={addUserLoading} iconName="UserPlus" iconPosition="left">
                  Create User
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* ============================================================ */}
      {/* RESET PASSWORD MODAL */}
      {/* ============================================================ */}
      {showResetPassword && resetPasswordTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-warning/10 flex items-center justify-center">
                  <Icon name="KeyRound" size={18} className="text-warning" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-foreground">Reset Password</h2>
                  <p className="text-xs text-muted-foreground truncate max-w-[180px]">{resetPasswordTarget?.full_name || resetPasswordTarget?.email}</p>
                </div>
              </div>
              <button
                onClick={() => setShowResetPassword(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={16} />
              </button>
            </div>
            <form onSubmit={handleResetPassword} className="px-6 py-5 space-y-4">
              <div className="flex items-start gap-3 p-3 rounded-xl bg-warning/5 border border-warning/20">
                <Icon name="AlertTriangle" size={15} className="text-warning flex-shrink-0 mt-0.5" />
                <p className="text-xs text-muted-foreground">
                  You are about to reset the password for <span className="font-semibold text-foreground">{resetPasswordTarget?.email}</span>. The user will need to use the new password to sign in.
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1.5">New Password <span className="text-error">*</span></label>
                <div className="relative">
                  <input
                    type={showResetPasswordInput ? 'text' : 'password'}
                    value={resetPasswordValue}
                    onChange={e => { setResetPasswordValue(e?.target?.value); setResetPasswordErr(''); }}
                    placeholder="Minimum 8 characters"
                    className="w-full h-10 px-3 pr-10 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-colors"
                    required
                    minLength={8}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowResetPasswordInput(p => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Icon name={showResetPasswordInput ? 'EyeOff' : 'Eye'} size={15} />
                  </button>
                </div>
                {resetPasswordValue?.length > 0 && resetPasswordValue?.length < 8 && (
                  <p className="text-xs text-warning mt-1">Password must be at least 8 characters</p>
                )}
              </div>
              {resetPasswordErr && (
                <div className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-xs border bg-error/10 border-error/30 text-error">
                  <Icon name="AlertCircle" size={13} />
                  {resetPasswordErr}
                </div>
              )}
              <div className="flex gap-3 pt-1">
                <Button type="button" variant="outline" className="flex-1" onClick={() => setShowResetPassword(false)} disabled={resetPasswordLoading}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-warning hover:bg-warning/90 text-white border-warning"
                  loading={resetPasswordLoading}
                  iconName="KeyRound"
                  iconPosition="left"
                >
                  Reset Password
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default RolePermissionManagement;
